import { ActivatedRouteSnapshot, RouterStateSnapshot, ResolveFn } from "@angular/router";
import { inject, Injector, LOCALE_ID, runInInjectionContext } from "@angular/core";
import { AppService } from "./app.service";
import { ApiGetResult } from "@upupa/data";

type ResolverParam = {
    resolverKey: string;
    resolverFn: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot, prev?: any) => Promise<any>;
    mapper?: <T, R>(res: ApiGetResult<T>) => R | ((res: ApiGetResult<T>) => Promise<R>);
};
export const pageResolverMetaMapper = (keys: string[]): ResolverParam => ({
    resolverKey: "meta",
    resolverFn: async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot, data) => {
        const m = {} as any;

        for (const key of keys.filter((k) => k in data)) {
            if (!data[key]) continue;
            const item = data[key];

            let { meta, lang } = item;
            if (!meta) {
                meta = item;
                lang ??= inject(LOCALE_ID);
            }
            const title = meta.title || item.title || m.title;
            const description = meta.description || item.description || m.description;
            const keywords = meta.keywords || item.keywords || m.keywords;
            let image = meta.image || item.image || m.image;
            if (Array.isArray(image)) {
                image = image?.[0]?.path;
            }
            const essentials = {
                title,
                description,
                keywords,
                image,
                og: {
                    ...(meta?.["og"] || m?.["og"] || {}),
                    "og:title": meta?.["og"]?.["og:title"] || title,
                    "og:description": meta?.["og"]?.["og:description"] || description,
                    "og:type": meta?.["og"]?.["og:type"] || "website",
                    "og:locale": meta?.["og"]?.["og:locale"] || lang || "en_US",
                    "og:image": meta?.["og"]?.["og:image"] || image,
                },
                twitter: {
                    ...(meta?.["twitter"] || m?.["twitter"] || {}),
                    "twitter:title": meta?.["twitter"]?.["twitter:title"] || title,
                    "twitter:description": meta?.["twitter"]?.["twitter:description"] || description,
                    "twitter:card": meta?.["twitter"]?.["twitter:card"] || "summary_large_image",
                    "twitter:image": meta?.["twitter"]?.["twitter:image"] || image,
                },
                schema: meta?.["schema"] || m?.["schema"] ? { ...(meta?.["schema"] || m?.["schema"]) } : undefined,
            };

            console.log("Page resolver meta item:", key, meta, m, essentials);
            Object.assign(m, essentials);
        }

        return m;
    },
    mapper: (res: any) => {
        // console.log("Page resolver meta mapper result:", res);

        return res;
    },
});

export function multiRequestsResolver(resolvers: ResolverParam[] = [], pipe = true): ResolveFn<Promise<Record<string, any>>> {
    return async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
        const resolvedData: Record<string, any> = {};
        const injector = inject(Injector);
        if (pipe === true) {
            for (const resolver of resolvers) {
                try {
                    const resolverFn = resolver.resolverFn;
                    const mapper = resolver.mapper ?? ((res: ApiGetResult<any>) => res);
                    resolvedData[resolver.resolverKey] = await runInInjectionContext(injector, async () => {
                        const result = await resolverFn(route, state, resolvedData);
                        return await mapper(result);
                    });
                } catch (error) {
                    console.error(`Error resolving ${resolver.resolverKey}:`, error);
                    resolvedData[resolver.resolverKey] = { error };
                }
            }
            return resolvedData;
        }

        return Promise.allSettled(
            resolvers.map((resolver) => {
                const resolverFn = resolver.resolverFn;
                const mapper = resolver.mapper ?? ((res: ApiGetResult<any>) => res);
                return resolverFn(route, state).then(mapper);
            }),
        ).then((report) => {
            report.forEach((result, index) => {
                const resolverKey = resolvers[index].resolverKey;
                if (result.status === "fulfilled") {
                    resolvedData[resolverKey] = result.value;
                }
                if (result.status === "rejected") {
                    console.error(`Resolver ${resolverKey} failed:`, result.reason);
                    resolvedData[resolverKey] = { error: result.reason };
                }
            });
            return resolvedData;
        });
    };
}

export function pageResolver(
    metaPath: (snapshot?: { route?: ActivatedRouteSnapshot; state?: RouterStateSnapshot }) => string,
    content?: {
        key: string;
        path: (snapshot?: { route?: ActivatedRouteSnapshot; state?: RouterStateSnapshot }) => string;
        mapper?: <T, R>(res: ApiGetResult<T>) => R;
    },
) {
    const resolvers: ResolverParam[] = [
        {
            resolverKey: "meta",
            resolverFn: async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
                const appService = inject(AppService);
                const injector = inject(Injector);
                const metaUrl = runInInjectionContext(injector, () => metaPath({ route, state }));
                const meta = await appService.fetchJsonAsset(metaUrl);

                return meta;
            },
            mapper: (res: ApiGetResult<any>) => res.data?.[0] ?? {},
        },
    ];
    if (content) {
        resolvers.push({
            resolverKey: content.key,
            resolverFn: async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
                const appService = inject(AppService);
                const injector = inject(Injector);
                const contentUrl = runInInjectionContext(injector, () => content.path({ route, state }));

                const contentData = await appService.fetchJsonAsset(contentUrl).then((res) => res.data);
                return contentData;
            },
            mapper: content.mapper ?? ((res: ApiGetResult<any>) => res.data?.[0]),
        });
    }
    resolvers.push(pageResolverMetaMapper(["meta", content?.key]));
    return multiRequestsResolver(resolvers);
}
